// S4.2 — THE CONTROL POINT EDITOR (spec 07 §4.2). One component, two routes:
// `/quality/control-points/new` (create version 1, optionally seeded from
// another point via `?from=`) and `/quality/control-points/$id/edit`.
//
// THE VERSION FLIP IS THE SERVER'S, AND THIS SCREEN DRIVES IT (§3.1 step 5).
// There is no PUT anywhere in §5: editing a used point POSTs the WHOLE
// definition to `/:id/versions`, and the server creates version N+1 with cloned
// bindings and check items, flips the old row `is_current = false,
// superseded_by_id = new`, and leaves open inspections executing their PINNED
// version. So the editor loads the current version, lets it be changed, and
// posts it back whole — and the banner says out loud what saving will do,
// because "Save" on a form that silently mints a new immutable revision is a
// button whose consequence the user cannot see.
//
// THE STAGE LOCKS AFTER SAVE, as §4.2 requires, and for a reason worth stating:
// the stage decides which BINDINGS are legal (§2.2 — receiving binds material
// and/or supplier, final binds a product, in-process binds nowhere here), so a
// stage change on an existing chain would silently invalidate every binding the
// versions before it carried. A new point is the honest way to change it.
//
// VALIDATION IS THE CONTRACT'S, RESTATED — not invented. Every inline rule here
// has a twin in `packages/contracts/src/quality/schemas.ts` and a §5 problem
// code behind it: `QUALITY_CHECK_ITEM_INVALID` (a measurement without a uom or
// a bound, min > max, a checklist with no rows, duplicate `seq`),
// `QUALITY_SAMPLING_PLAN_INVALID` (a mode without its parameters, or parameters
// without their mode) and `QUALITY_BINDING_STAGE_INVALID`. The client checks
// them so the operator is not made to round-trip for a typo; the server is
// still the one that decides, and its refusal is rendered when it disagrees.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SOURCE_LOCALE, SUPPORTED_LOCALES, type Locale } from "@assessly/contracts/locales";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { CardSkeleton, ErrorState, ForbiddenState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { useStickyOpen } from "../../../../lib/use-sticky-open";
import { requestIdOf } from "../../../../lib/request";
import { isExactMeasurement } from "../lib/grading";
import { useQualityPermissions } from "../hooks/permissions";
import {
  useCreateQualityControlPoint,
  useQualityBindableProducts,
  useQualityControlPoint,
  useVersionQualityControlPoint,
  type QualityCheckType,
  type QualityControlPoint,
  type QualityControlPointCreateRequest,
  type QualitySampleMode,
  type QualityStage,
} from "../hooks/quality-queries";

export interface ControlPointNewSearch {
  from?: string;
}

export function validateControlPointNewSearch(
  search: Record<string, unknown>,
): ControlPointNewSearch {
  const from = search["from"];
  return typeof from === "string" && from.length > 0 ? { from } : {};
}

/** The editor's own draft shape. Deliberately all-strings for every numeric
 * field: these are `numeric` columns on the wire as EXACT DECIMAL TEXT (D-041),
 * and a form that parsed them into JS numbers on the way in would round-trip
 * `2.100000` as `2.1` and a min of `0.65` as something that is not `0.65`. */
export interface CheckItemDraft {
  readonly key: string;
  name: string;
  /**
   * The name's NON-ENGLISH locales, carried through an edit without a box on
   * screen (#128, migration 0042).
   *
   * §4.2 has one name field and it is the English one. Since 0042 the row holds
   * a `{en, ru, zh, fr}` bundle, and versioning a PACK point through this editor
   * re-creates the row from this draft — so without this field a qc lead fixing
   * an English typo would silently DELETE the Russian, Chinese and French names
   * the vertical pack shipped, which is the exact defect 0042 exists to end,
   * re-introduced through a screen that looks unrelated to it. Loaded from the
   * point, sent back untouched, empty for a point the tenant is authoring now.
   * (A translation EDITOR is §4.2 work and is not this.)
   */
  readonly name_i18n: Readonly<Record<string, string>>;
  check_type: QualityCheckType;
  uom: string;
  min_value: string;
  target_value: string;
  max_value: string;
  checklist_keys: string;
  photo_required: boolean;
  min_photos: string;
  is_required: boolean;
  appears_on_coa: boolean;
}

export interface BindingDraft {
  readonly key: string;
  material_id: string;
  product_id: string;
}

export interface ControlPointDraft {
  code: string;
  name: string;
  /**
   * The name's NON-ENGLISH locales, carried through an edit without a box on
   * screen (#128, migration 0042).
   *
   * §4.2 has one name field and it is the English one. Since 0042 the row holds
   * a `{en, ru, zh, fr}` bundle, and versioning a PACK point through this editor
   * re-creates the row from this draft — so without this field a qc lead fixing
   * an English typo would silently DELETE the Russian, Chinese and French names
   * the vertical pack shipped, which is the exact defect 0042 exists to end,
   * re-introduced through a screen that looks unrelated to it. Loaded from the
   * point, sent back untouched, empty for a point the tenant is authoring now.
   * (A translation EDITOR is §4.2 work and is not this.)
   */
  readonly name_i18n: Readonly<Record<string, string>>;
  stage: QualityStage;
  sample_mode: QualitySampleMode;
  sample_size: string;
  sample_pct: string;
  aql_inspection_level: string;
  aql_value: string;
  is_blocking: boolean;
  auto_hold_on_fail: boolean;
  allow_guided_cut: boolean;
  cut_trim_allowance_m: string;
  min_sellable_length_m: string;
  instructions: Record<string, string>;
  bindings: BindingDraft[];
  items: CheckItemDraft[];
}

/** A name bundle's non-English half. `en` is dropped on the way in because the
 * name BOX owns it and a second copy is a second thing to keep in step; the
 * server puts it back from `name` (see `nameBundle` in the quality controller). */
function otherLocales(
  bundle: Readonly<Record<string, string | undefined>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const locale of SUPPORTED_LOCALES) {
    if (locale === SOURCE_LOCALE) continue;
    const value = bundle[locale];
    if (value !== undefined && value.length > 0) out[locale] = value;
  }
  return out;
}

/** The instruction boxes always on the form: English and the launch
 * market's Russian; the rest sit behind "Other languages". */
const INSTRUCTIONS_SHOWN: ReadonlySet<Locale> = new Set<Locale>([SOURCE_LOCALE, "ru"]);

let seq = 0;
const nextKey = (): string => {
  seq += 1;
  return `d${String(seq)}`;
};

export function emptyDraft(): ControlPointDraft {
  return {
    code: "",
    name: "",
    name_i18n: {},
    stage: "in_process",
    sample_mode: "every_unit",
    sample_size: "",
    sample_pct: "",
    aql_inspection_level: "II",
    aql_value: "2.5",
    is_blocking: true,
    auto_hold_on_fail: true,
    allow_guided_cut: false,
    cut_trim_allowance_m: "0.500",
    min_sellable_length_m: "",
    instructions: { en: "", ru: "", zh: "", fr: "" },
    bindings: [],
    items: [
      {
        key: nextKey(),
        name: "",
        name_i18n: {},
        check_type: "pass_fail",
        uom: "",
        min_value: "",
        target_value: "",
        max_value: "",
        checklist_keys: "",
        photo_required: false,
        min_photos: "1",
        is_required: true,
        appears_on_coa: false,
      },
    ],
  };
}

/** Load an existing definition into the draft. Used by BOTH the edit route and
 * `?from=` (duplicate), which differ only in whether the code is carried over —
 * a duplicate MUST take a new code (`UNIQUE (tenant, code, version)`), and
 * carrying it would 409 on save with nothing on screen explaining why. */
export function draftFromPoint(
  point: QualityControlPoint,
  mode: "edit" | "duplicate",
): ControlPointDraft {
  return {
    code: mode === "edit" ? point.code : "",
    // The English box reads `name_i18n.en` through the flat projection; the
    // other three ride along in `name_i18n` (see the field's own comment).
    name: point.name,
    name_i18n: otherLocales(point.name_i18n),
    stage: point.stage,
    sample_mode: point.sample_mode,
    sample_size: point.sample_size === null ? "" : String(point.sample_size),
    sample_pct: point.sample_pct ?? "",
    aql_inspection_level: point.aql_inspection_level ?? "II",
    aql_value: point.aql_value ?? "2.5",
    is_blocking: point.is_blocking,
    auto_hold_on_fail: point.auto_hold_on_fail,
    allow_guided_cut: point.allow_guided_cut,
    cut_trim_allowance_m: point.cut_trim_allowance_m,
    min_sellable_length_m: point.min_sellable_length_m ?? "",
    instructions: {
      en: point.instructions_i18n.en ?? "",
      ru: point.instructions_i18n.ru ?? "",
      zh: point.instructions_i18n.zh ?? "",
      fr: point.instructions_i18n.fr ?? "",
    },
    bindings: point.bindings.map((binding) => ({
      key: nextKey(),
      material_id: binding.material_id ?? "",
      product_id: binding.product_id ?? "",
    })),
    items: [...point.check_items]
      .sort((a, b) => a.seq - b.seq)
      .map((item) => ({
        key: nextKey(),
        name: item.name,
        name_i18n: otherLocales(item.name_i18n),
        check_type: item.check_type,
        uom: item.uom ?? "",
        min_value: item.min_value ?? "",
        target_value: item.target_value ?? "",
        max_value: item.max_value ?? "",
        checklist_keys: (item.checklist_items_i18n ?? [])
          .map((row) => {
            const key = (row as Record<string, unknown>)["key"];
            return typeof key === "string" ? key : "";
          })
          .filter((k) => k.length > 0)
          .join(", "),
        photo_required: item.photo_required,
        min_photos: String(item.min_photos),
        is_required: item.is_required,
        appears_on_coa: item.appears_on_coa,
      })),
  };
}

/** Inline validation, as a table of dotted paths → message keys. PURE, so the
 * whole rule set is a unit test rather than a form somebody has to click
 * through. Every entry mirrors a contract refusal (see the header). */
export function validateDraft(draft: ControlPointDraft): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!/^[A-Z][A-Z0-9_-]{1,47}$/u.test(draft.code)) errors["code"] = "quality.editor.errCode";
  if (draft.name.trim().length === 0) errors["name"] = "quality.editor.errName";

  const needsSize = draft.sample_mode === "fixed_count" || draft.sample_mode === "first_n";
  if (needsSize && !/^\d+$/u.test(draft.sample_size)) {
    errors["sample_size"] = "quality.editor.errSampleSize";
  }
  if (draft.sample_mode === "percentage") {
    const pct = Number(draft.sample_pct);
    if (!/^\d{1,3}(\.\d{1,2})?$/u.test(draft.sample_pct) || pct <= 0 || pct > 100) {
      errors["sample_pct"] = "quality.editor.errSamplePct";
    }
  }
  // §2.2: receiving binds material and/or supplier and NEVER a product; final
  // binds a product; an in-process point is bound on module 01's routing step
  // and carries no binding here at all.
  if (draft.stage === "in_process" && draft.bindings.length > 0) {
    errors["bindings"] = "quality.editor.errInProcessBindings";
  }
  if (draft.stage === "final") {
    if (draft.bindings.length === 0) errors["bindings"] = "quality.editor.errFinalBindings";
    draft.bindings.forEach((binding, index) => {
      if (binding.product_id.trim().length === 0) {
        errors[`bindings.${String(index)}`] = "quality.editor.errBindingProduct";
      }
    });
  }
  if (draft.stage === "receiving") {
    if (draft.bindings.length === 0) errors["bindings"] = "quality.editor.errReceivingBindings";
    draft.bindings.forEach((binding, index) => {
      if (binding.material_id.trim().length === 0) {
        errors[`bindings.${String(index)}`] = "quality.editor.errBindingMaterial";
      }
    });
  }

  if (draft.items.length === 0) errors["items"] = "quality.editor.errNoItems";
  const names = new Set<string>();
  draft.items.forEach((item, index) => {
    const path = `items.${String(index)}`;
    if (item.name.trim().length === 0) errors[`${path}.name`] = "quality.editor.errItemName";
    if (names.has(item.name.trim())) errors[`${path}.name`] = "quality.editor.errItemDuplicate";
    names.add(item.name.trim());
    if (item.check_type === "measurement") {
      if (item.uom.trim().length === 0) errors[`${path}.uom`] = "quality.editor.errItemUom";
      if (item.min_value.length === 0 && item.max_value.length === 0) {
        errors[`${path}.bounds`] = "quality.editor.errItemBounds";
      }
      for (const field of ["min_value", "target_value", "max_value"] as const) {
        const raw = item[field];
        if (raw.length > 0 && !isExactMeasurement(raw)) {
          errors[`${path}.${field}`] = "quality.editor.errItemDecimal";
        }
      }
      if (
        item.min_value.length > 0 &&
        item.max_value.length > 0 &&
        isExactMeasurement(item.min_value) &&
        isExactMeasurement(item.max_value) &&
        Number(item.min_value) > Number(item.max_value)
      ) {
        errors[`${path}.bounds`] = "quality.editor.errItemMinMax";
      }
    }
    if (item.check_type === "checklist" && parseChecklistKeys(item.checklist_keys).length === 0) {
      errors[`${path}.checklist`] = "quality.editor.errItemChecklist";
    }
    if (!/^\d+$/u.test(item.min_photos) || Number(item.min_photos) < 1) {
      errors[`${path}.min_photos`] = "quality.editor.errItemPhotos";
    }
  });
  return errors;
}

export function parseChecklistKeys(raw: string): string[] {
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Draft → the §5 create/version body. `seq` is assigned from the LIST ORDER,
 * which is what the reorder controls actually change: §2.3's `seq` is the
 * execution order a worker follows top to bottom, so it is derived from the
 * order on screen and never typed. */
export function draftToRequest(draft: ControlPointDraft): QualityControlPointCreateRequest {
  const instructions: Record<string, string> = {};
  for (const [locale, text] of Object.entries(draft.instructions)) {
    if (text.trim().length > 0) instructions[locale] = text.trim();
  }
  const needsSize = draft.sample_mode === "fixed_count" || draft.sample_mode === "first_n";
  return {
    code: draft.code,
    name: draft.name.trim(),
    ...(Object.keys(draft.name_i18n).length > 0 ? { name_i18n: draft.name_i18n } : {}),
    stage: draft.stage,
    sample_mode: draft.sample_mode,
    ...(needsSize ? { sample_size: Number(draft.sample_size) } : {}),
    ...(draft.sample_mode === "percentage" ? { sample_pct: draft.sample_pct } : {}),
    ...(draft.sample_mode === "aql"
      ? {
          aql_inspection_level: draft.aql_inspection_level as NonNullable<
            QualityControlPointCreateRequest["aql_inspection_level"]
          >,
          aql_value: draft.aql_value as NonNullable<QualityControlPointCreateRequest["aql_value"]>,
        }
      : {}),
    is_blocking: draft.is_blocking,
    auto_hold_on_fail: draft.auto_hold_on_fail,
    allow_guided_cut: draft.allow_guided_cut,
    cut_trim_allowance_m: draft.cut_trim_allowance_m,
    ...(draft.allow_guided_cut && draft.min_sellable_length_m.length > 0
      ? { min_sellable_length_m: draft.min_sellable_length_m }
      : {}),
    ...(Object.keys(instructions).length > 0 ? { instructions_i18n: instructions } : {}),
    ...(draft.stage === "in_process"
      ? {}
      : {
          bindings: draft.bindings.map((binding) => ({
            ...(binding.material_id.trim().length > 0
              ? { material_id: binding.material_id.trim() }
              : {}),
            ...(binding.product_id.trim().length > 0
              ? { product_id: binding.product_id.trim() }
              : {}),
          })),
        }),
    check_items: draft.items.map((item, index) => ({
      seq: index,
      name: item.name.trim(),
      ...(Object.keys(item.name_i18n).length > 0 ? { name_i18n: item.name_i18n } : {}),
      check_type: item.check_type,
      ...(item.check_type === "measurement" && item.uom.trim().length > 0
        ? { uom: item.uom.trim() }
        : {}),
      ...(item.check_type === "measurement" && item.min_value.length > 0
        ? { min_value: item.min_value }
        : {}),
      ...(item.check_type === "measurement" && item.target_value.length > 0
        ? { target_value: item.target_value }
        : {}),
      ...(item.check_type === "measurement" && item.max_value.length > 0
        ? { max_value: item.max_value }
        : {}),
      ...(item.check_type === "checklist"
        ? {
            checklist_items_i18n: parseChecklistKeys(item.checklist_keys).map((key) => ({
              key,
              label: { en: key },
            })),
          }
        : {}),
      photo_required: item.photo_required,
      min_photos: Number(item.min_photos),
      is_required: item.is_required,
      appears_on_coa: item.appears_on_coa,
    })),
  };
}

/**
 * FOLDED CHECKS (W-SWEEP height, D-451). A loaded definition with many checks
 * drew every one as an open card, and the sweep measured the editor at 11,172
 * px, 12 screens, for a point whose checks nobody was changing. A point with
 * more than this many checks opens with each one folded to a single line (its
 * number, name and kind); one press opens it. A point with this many or
 * fewer opens as before, so the one-check edit that M07-S12 walks is
 * unchanged, and a check added in this session always opens.
 */
export const FOLD_CHECKS_ABOVE = 3;

/** The checks a freshly loaded draft opens folded. */
export function initiallyFolded(keys: readonly string[]): Set<string> {
  return keys.length > FOLD_CHECKS_ABOVE ? new Set(keys) : new Set();
}

/**
 * FOLDED BINDINGS (W-SWEEP height, lane 2 on 2026-10-16). A final point bound
 * to many products drew one picker per product, and the sweep measured the
 * editor at 13,226 px for a point nobody was rebinding. A list longer than
 * this opens with its first rows and a "Show all" button; a row a validation
 * message names stays on screen, and adding a row shows the whole list so the
 * new picker is never hidden.
 */
export const FOLD_BINDINGS_ABOVE = 5;

/** The binding rows on screen, by index into the draft's list. */
export function visibleBindings(
  count: number,
  showAll: boolean,
  errors: Readonly<Record<string, string>>,
): number[] {
  const all = Array.from({ length: count }, (_, i) => i);
  if (showAll || count <= FOLD_BINDINGS_ABOVE) return all;
  return all.filter((i) => i < FOLD_BINDINGS_ABOVE || `bindings.${String(i)}` in errors);
}

/** True when a validation message names check `index`, so a folded check
 * with a problem opens itself rather than hiding the message. */
export function checkHasError(errors: Readonly<Record<string, string>>, index: number): boolean {
  const prefix = `items.${String(index)}.`;
  return Object.keys(errors).some((path) => path.startsWith(prefix));
}

export function ControlPointEditorScreen({ mode }: { mode: "new" | "edit" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = useQualityPermissions();
  const params = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const sourceId = mode === "edit" ? params.id : search.from;

  const source = useQualityControlPoint(sourceId, sourceId !== undefined);
  const [draft, setDraft] = useState<ControlPointDraft>(emptyDraft);
  const [loadedFrom, setLoadedFrom] = useState<string | undefined>(undefined);
  const [dirty, setDirty] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [allBindings, setAllBindings] = useState(false);
  // Rev-7: the languages past English and Russian open themselves when one
  // holds text (also once a loaded point fills the draft) and never close
  // themselves, so emptying the last box does not fold it under the cursor.
  const moreLanguages = useStickyOpen(
    SUPPORTED_LOCALES.some(
      (l) => !INSTRUCTIONS_SHOWN.has(l) && (draft.instructions[l] ?? "") !== "",
    ),
  );

  const create = useCreateQualityControlPoint();
  const version = useVersionQualityControlPoint();
  // §2.2 decides WHICH catalogue side a binding may name; an in-process point
  // binds nothing here at all, so the read is not issued for one.
  const bindableProducts = useQualityBindableProducts(
    draft.stage === "receiving" ? "raw_material" : "finished_good",
    draft.stage !== "in_process",
  );
  const bindables = bindableProducts.data ?? [];
  const bindableIds = bindables.map((row) => row.id);
  const busy = create.isPending || version.isPending;

  useEffect(() => {
    const point = source.data;
    if (point === undefined || loadedFrom === point.id) return;
    const loaded = draftFromPoint(point, mode === "edit" ? "edit" : "duplicate");
    setDraft(loaded);
    setFolded(initiallyFolded(loaded.items.map((item) => item.key)));
    setLoadedFrom(point.id);
  }, [source.data, loadedFrom, mode]);

  // THE UNSAVED-CHANGES GUARD (§4.2). A definition is 20 fields and a checklist;
  // losing it to a stray back-gesture on a tablet is the kind of loss people
  // stop using an editor over. `beforeunload` is the only guard the platform
  // offers that a router navigation cannot silently skip.
  useEffect(() => {
    if (!dirty) return undefined;
    const onLeave = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", onLeave);
    return () => {
      window.removeEventListener("beforeunload", onLeave);
    };
  }, [dirty]);

  const errors = useMemo(() => validateDraft(draft), [draft]);
  const errorOf = (path: string): string | undefined =>
    showErrors && errors[path] !== undefined ? t(errors[path]) : undefined;

  const patch = (next: Partial<ControlPointDraft>): void => {
    setDirty(true);
    setDraft((prior) => ({ ...prior, ...next }));
  };
  const patchItem = (index: number, next: Partial<CheckItemDraft>): void => {
    setDirty(true);
    setDraft((prior) => ({
      ...prior,
      items: prior.items.map((item, i) => (i === index ? { ...item, ...next } : item)),
    }));
  };
  const moveItem = (index: number, delta: number): void => {
    const target = index + delta;
    if (target < 0 || target >= draft.items.length) return;
    setDirty(true);
    setDraft((prior) => {
      const items = [...prior.items];
      const [moved] = items.splice(index, 1);
      if (moved !== undefined) items.splice(target, 0, moved);
      return { ...prior, items };
    });
  };

  if (!perms.isResolved) {
    return <CardSkeleton lines={8} label={t("quality.common.loading")} />;
  }
  if (!perms.has("quality.catalog.manage")) {
    return <ForbiddenState title={t("quality.access.title")} body={t("quality.access.editor")} />;
  }
  if (sourceId !== undefined && source.isPending) {
    return <CardSkeleton lines={8} label={t("quality.common.loading")} />;
  }
  if (sourceId !== undefined && source.error !== null) {
    return (
      <ErrorState
        title={t("quality.editor.loadError")}
        retryLabel={t("quality.common.retry")}
        requestIdLabel={t("quality.common.requestId")}
        requestId={requestIdOf(source.error)}
        onRetry={() => {
          void source.refetch();
        }}
      />
    );
  }

  const save = (): void => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    const body = draftToRequest(draft);
    const done = (): void => {
      setDirty(false);
      void navigate({ to: "/quality/control-points", search: {} });
    };
    if (mode === "edit" && params.id !== undefined) {
      version.mutate({ id: params.id, body }, { onSuccess: done });
    } else {
      create.mutate(body, { onSuccess: done });
    }
  };

  const currentVersion = source.data?.version;
  const stageLocked = mode === "edit";

  const instructionsField = (locale: Locale) => (
    <Field key={locale} label={t(`quality.locale.${locale}`)} wide>
      {/* A TEXTAREA, not an input. `instructions_i18n` holds up to 4000
            characters of operator prose (§2.1) and it is rendered on the
            floor above the checks — the #85 sweep measured the single-line
            version at 1162 px against 2041 px of content in three locales
            at once, which is a sentence a supervisor cannot read back
            while writing it. A textarea WRAPS, which is the property that
            makes both problems the same fix. */}
      <textarea
        rows={2}
        value={draft.instructions[locale] ?? ""}
        data-testid={`quality-instructions-${locale}`}
        onChange={(e) => {
          patch({ instructions: { ...draft.instructions, [locale]: e.target.value } });
        }}
      />
    </Field>
  );

  return (
    <section className="quality-editor" data-testid="quality-editor">
      <PageHeader
        crumbs={[{ label: t("quality.library.title"), to: "/quality/control-points" }]}
        title={mode === "edit" ? t("quality.editor.editTitle") : t("quality.editor.newTitle")}
      />

      {/* §4.2's version banner. It says the CONSEQUENCE, not the action: a Save
          that mints an immutable revision N+1 and supersedes the one every open
          inspection pinned is not a Save anybody should have to infer. */}
      {mode === "edit" && currentVersion !== undefined ? (
        <p className="quality-banner" role="status" data-testid="quality-version-banner">
          {t("quality.editor.versionBanner", { next: currentVersion + 1 })}
        </p>
      ) : null}
      {mode === "new" && search.from !== undefined ? (
        <p className="quality-banner" role="status" data-testid="quality-duplicate-banner">
          {t("quality.editor.duplicateBanner")}
        </p>
      ) : null}

      <div className="quality-form">
        <Field label={t("quality.editor.code")}>
          <input
            value={draft.code}
            disabled={mode === "edit"}
            data-testid="quality-editor-code"
            onChange={(e) => {
              patch({ code: e.target.value.toUpperCase() });
            }}
          />
          <FieldError message={errorOf("code")} testId="quality-err-code" />
        </Field>
        <Field label={t("quality.editor.name")} wide>
          <input
            value={draft.name}
            data-testid="quality-editor-name"
            onChange={(e) => {
              patch({ name: e.target.value });
            }}
          />
          <FieldError message={errorOf("name")} testId="quality-err-name" />
        </Field>
        <Field label={t("quality.editor.stage")}>
          <select
            value={draft.stage}
            disabled={stageLocked}
            data-testid="quality-editor-stage"
            onChange={(e) => {
              patch({ stage: e.target.value as QualityStage, bindings: [] });
            }}
          >
            {(["receiving", "in_process", "final"] as const).map((value) => (
              <option key={value} value={value}>
                {t(`quality.stage.${value}`)}
              </option>
            ))}
          </select>
          {stageLocked ? (
            <span className="quality-hint" data-testid="quality-stage-locked">
              {t("quality.editor.stageLocked")}
            </span>
          ) : null}
        </Field>
      </div>

      {/* ---- sampling (§2.1) ---- */}
      <fieldset className="quality-group">
        <legend>{t("quality.editor.sampling")}</legend>
        <div className="quality-form">
          <Field label={t("quality.editor.sampleMode")}>
            <select
              value={draft.sample_mode}
              data-testid="quality-editor-sample-mode"
              onChange={(e) => {
                patch({ sample_mode: e.target.value as QualitySampleMode });
              }}
            >
              {(["every_unit", "fixed_count", "percentage", "first_n", "aql"] as const).map(
                (value) => (
                  <option key={value} value={value}>
                    {t(`quality.sampleMode.${value}`)}
                  </option>
                ),
              )}
            </select>
          </Field>
          {draft.sample_mode === "fixed_count" || draft.sample_mode === "first_n" ? (
            <Field label={t("quality.editor.sampleSize")}>
              <input
                inputMode="numeric"
                value={draft.sample_size}
                data-testid="quality-editor-sample-size"
                onChange={(e) => {
                  patch({ sample_size: e.target.value });
                }}
              />
              <FieldError message={errorOf("sample_size")} testId="quality-err-sample-size" />
            </Field>
          ) : null}
          {draft.sample_mode === "percentage" ? (
            <Field label={t("quality.editor.samplePct")}>
              <input
                inputMode="decimal"
                value={draft.sample_pct}
                data-testid="quality-editor-sample-pct"
                onChange={(e) => {
                  patch({ sample_pct: e.target.value });
                }}
              />
              <FieldError message={errorOf("sample_pct")} testId="quality-err-sample-pct" />
            </Field>
          ) : null}
          {draft.sample_mode === "aql" ? (
            <>
              <Field label={t("quality.editor.aqlLevel")}>
                <select
                  value={draft.aql_inspection_level}
                  data-testid="quality-editor-aql-level"
                  onChange={(e) => {
                    patch({ aql_inspection_level: e.target.value });
                  }}
                >
                  {(["I", "II", "III"] as const).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("quality.editor.aqlValue")}>
                <select
                  value={draft.aql_value}
                  data-testid="quality-editor-aql-value"
                  onChange={(e) => {
                    patch({ aql_value: e.target.value });
                  }}
                >
                  {(["0.65", "1.0", "1.5", "2.5", "4.0", "6.5"] as const).map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          ) : null}
        </div>
      </fieldset>

      {/* ---- failure behaviour (§2.1) ---- */}
      <fieldset className="quality-group">
        <legend>{t("quality.editor.behaviour")}</legend>
        <label className="quality-toggle">
          <input
            type="checkbox"
            checked={draft.is_blocking}
            data-testid="quality-editor-blocking"
            onChange={(e) => {
              patch({ is_blocking: e.target.checked });
            }}
          />
          <span>{t("quality.editor.blocking")}</span>
        </label>
        {/* D-123 ruling 5: what "blocking in-process" actually fences is REWORK
            on a re-opened step, not the next operation. Saying so at the toggle
            is the difference between a factory configuring a fence they have
            and one they imagine. */}
        <p className="quality-hint" data-testid="quality-blocking-hint">
          {t(`quality.editor.blockingHint.${draft.stage}`)}
        </p>
        <label className="quality-toggle">
          <input
            type="checkbox"
            checked={draft.auto_hold_on_fail}
            data-testid="quality-editor-auto-hold"
            onChange={(e) => {
              patch({ auto_hold_on_fail: e.target.checked });
            }}
          />
          <span>{t("quality.editor.autoHold")}</span>
        </label>
        <p className="quality-hint" data-testid="quality-auto-hold-hint">
          {t("quality.editor.autoHoldHint")}
        </p>
        <label className="quality-toggle">
          <input
            type="checkbox"
            checked={draft.allow_guided_cut}
            data-testid="quality-editor-guided-cut"
            onChange={(e) => {
              patch({ allow_guided_cut: e.target.checked });
            }}
          />
          <span>{t("quality.editor.guidedCut")}</span>
        </label>
        {draft.allow_guided_cut ? (
          <div className="quality-form">
            <Field label={t("quality.editor.trimAllowance")}>
              <input
                inputMode="decimal"
                value={draft.cut_trim_allowance_m}
                data-testid="quality-editor-trim"
                onChange={(e) => {
                  patch({ cut_trim_allowance_m: e.target.value });
                }}
              />
            </Field>
            <Field label={t("quality.editor.minSellable")}>
              <input
                inputMode="decimal"
                value={draft.min_sellable_length_m}
                data-testid="quality-editor-min-sellable"
                onChange={(e) => {
                  patch({ min_sellable_length_m: e.target.value });
                }}
              />
            </Field>
          </div>
        ) : null}
      </fieldset>

      {/* ---- bindings (§2.2) ---- */}
      <fieldset className="quality-group">
        <legend>{t("quality.editor.bindings")}</legend>
        {draft.stage === "in_process" ? (
          <p className="quality-hint" data-testid="quality-bindings-routing">
            {t("quality.editor.bindingsOnRouting")}
          </p>
        ) : (
          <>
            {visibleBindings(draft.bindings.length, allBindings, showErrors ? errors : {}).map(
              (index) => {
                const binding = draft.bindings[index];
                if (binding === undefined) return null;
                return (
                  <div key={binding.key} className="quality-repeater-row">
                    <Field
                      label={
                        draft.stage === "receiving"
                          ? t("quality.editor.bindingMaterial")
                          : t("quality.editor.bindingProduct")
                      }
                      wide
                    >
                      {/* A PICKER, which is what §4.2 asks for and what the #85
                      sweep proved is the only shape that fits: the previous
                      paste-a-uuid box rendered 224 px wide against 307 px of
                      content at BOTH 1440 and 412 — a `control-too-small`
                      defect on every combination — and asked a qc lead to
                      know a material by its uuid. */}
                      <select
                        value={
                          draft.stage === "receiving" ? binding.material_id : binding.product_id
                        }
                        data-testid="quality-binding-id"
                        onChange={(e) => {
                          setDirty(true);
                          setDraft((prior) => ({
                            ...prior,
                            bindings: prior.bindings.map((row, i) =>
                              i === index
                                ? prior.stage === "receiving"
                                  ? { ...row, material_id: e.target.value }
                                  : { ...row, product_id: e.target.value }
                                : row,
                            ),
                          }));
                        }}
                      >
                        <option value="">{t("quality.editor.bindingHint")}</option>
                        {/* A binding the catalogue read has not resolved a NAME for
                        (a product outside the first page, or a read that
                        failed) keeps its own value as an option rather than
                        silently resetting the select to "pick one" — which
                        would drop a binding on save that nobody removed. */}
                        {bindableIds.includes(
                          draft.stage === "receiving" ? binding.material_id : binding.product_id,
                        ) ||
                        (draft.stage === "receiving" ? binding.material_id : binding.product_id)
                          .length === 0 ? null : (
                          <option
                            value={
                              draft.stage === "receiving" ? binding.material_id : binding.product_id
                            }
                          >
                            {t("quality.editor.bindingUnresolved")}
                          </option>
                        )}
                        {bindables.map((row) => (
                          <option key={row.id} value={row.id}>
                            {row.sku} · {row.name}
                          </option>
                        ))}
                      </select>
                      <FieldError
                        message={errorOf(`bindings.${String(index)}`)}
                        testId="quality-err-binding"
                      />
                    </Field>
                    <Button
                      size="sm"
                      data-testid="quality-binding-remove"
                      onClick={() => {
                        setDirty(true);
                        setDraft((prior) => ({
                          ...prior,
                          bindings: prior.bindings.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      {t("quality.editor.removeBinding")}
                    </Button>
                  </div>
                );
              },
            )}
            {!allBindings && draft.bindings.length > FOLD_BINDINGS_ABOVE ? (
              <Button
                size="sm"
                data-testid="quality-bindings-show-all"
                onClick={() => {
                  setAllBindings(true);
                }}
              >
                {t("quality.editor.bindingsShowAll", { count: draft.bindings.length })}
              </Button>
            ) : null}
            <Button
              size="sm"
              data-testid="quality-binding-add"
              onClick={() => {
                setDirty(true);
                setAllBindings(true);
                setDraft((prior) => ({
                  ...prior,
                  bindings: [
                    ...prior.bindings,
                    { key: nextKey(), material_id: "", product_id: "" },
                  ],
                }));
              }}
            >
              {t("quality.editor.addBinding")}
            </Button>
            <FieldError message={errorOf("bindings")} testId="quality-err-bindings" />
          </>
        )}
      </fieldset>

      {/* ---- check items (§2.3) ---- */}
      <fieldset className="quality-group">
        <legend>{t("quality.editor.checkItems")}</legend>
        <ol className="quality-items" data-testid="quality-editor-items">
          {draft.items.map((item, index) => {
            const isFolded = folded.has(item.key) && !(showErrors && checkHasError(errors, index));
            return (
              <li
                key={item.key}
                className="quality-item"
                data-testid="quality-editor-item"
                data-folded={isFolded ? "true" : "false"}
              >
                <div className="quality-item-head">
                  <StatusChip variant="neutral" dot={false}>
                    {t("quality.editor.itemSeq", { seq: index + 1 })}
                  </StatusChip>
                  {isFolded ? (
                    <span className="quality-item-summary" data-testid="quality-item-summary">
                      {item.name.trim().length > 0 ? item.name : t("quality.editor.itemUnnamed")}
                      {" · "}
                      {t(`quality.checkType.${item.check_type}`)}
                    </span>
                  ) : null}
                  <div className="quality-rowacts">
                    <Button
                      size="sm"
                      data-testid="quality-item-toggle"
                      aria-expanded={!isFolded}
                      onClick={() => {
                        setFolded((prior) => {
                          const next = new Set(prior);
                          if (isFolded) next.delete(item.key);
                          else next.add(item.key);
                          return next;
                        });
                      }}
                    >
                      {isFolded ? t("quality.editor.itemOpen") : t("quality.editor.itemFold")}
                    </Button>
                    <Button
                      size="sm"
                      disabled={index === 0}
                      data-testid="quality-item-up"
                      onClick={() => {
                        moveItem(index, -1);
                      }}
                    >
                      {t("quality.editor.moveUp")}
                    </Button>
                    <Button
                      size="sm"
                      disabled={index === draft.items.length - 1}
                      data-testid="quality-item-down"
                      onClick={() => {
                        moveItem(index, 1);
                      }}
                    >
                      {t("quality.editor.moveDown")}
                    </Button>
                    <Button
                      size="sm"
                      disabled={draft.items.length === 1}
                      data-testid="quality-item-remove"
                      onClick={() => {
                        setDirty(true);
                        setDraft((prior) => ({
                          ...prior,
                          items: prior.items.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      {t("quality.editor.removeItem")}
                    </Button>
                  </div>
                </div>

                {isFolded ? null : (
                  <>
                    <div className="quality-form">
                      <Field label={t("quality.editor.itemName")} wide>
                        <input
                          value={item.name}
                          data-testid="quality-item-name"
                          onChange={(e) => {
                            patchItem(index, { name: e.target.value });
                          }}
                        />
                        <FieldError
                          message={errorOf(`items.${String(index)}.name`)}
                          testId="quality-err-item-name"
                        />
                      </Field>
                      <Field label={t("quality.editor.itemType")}>
                        <select
                          value={item.check_type}
                          data-testid="quality-item-type"
                          onChange={(e) => {
                            patchItem(index, { check_type: e.target.value as QualityCheckType });
                          }}
                        >
                          {(["pass_fail", "measurement", "photo", "checklist"] as const).map(
                            (value) => (
                              <option key={value} value={value}>
                                {t(`quality.checkType.${value}`)}
                              </option>
                            ),
                          )}
                        </select>
                      </Field>
                    </div>

                    {item.check_type === "measurement" ? (
                      <div className="quality-form">
                        <Field label={t("quality.editor.itemUom")}>
                          <input
                            value={item.uom}
                            data-testid="quality-item-uom"
                            onChange={(e) => {
                              patchItem(index, { uom: e.target.value });
                            }}
                          />
                          <FieldError
                            message={errorOf(`items.${String(index)}.uom`)}
                            testId="quality-err-item-uom"
                          />
                        </Field>
                        <Field label={t("quality.editor.itemMin")}>
                          <input
                            inputMode="decimal"
                            value={item.min_value}
                            data-testid="quality-item-min"
                            onChange={(e) => {
                              patchItem(index, { min_value: e.target.value });
                            }}
                          />
                        </Field>
                        <Field label={t("quality.editor.itemTarget")}>
                          <input
                            inputMode="decimal"
                            value={item.target_value}
                            data-testid="quality-item-target"
                            onChange={(e) => {
                              patchItem(index, { target_value: e.target.value });
                            }}
                          />
                        </Field>
                        <Field label={t("quality.editor.itemMax")}>
                          <input
                            inputMode="decimal"
                            value={item.max_value}
                            data-testid="quality-item-max"
                            onChange={(e) => {
                              patchItem(index, { max_value: e.target.value });
                            }}
                          />
                        </Field>
                        <FieldError
                          message={
                            errorOf(`items.${String(index)}.bounds`) ??
                            errorOf(`items.${String(index)}.min_value`) ??
                            errorOf(`items.${String(index)}.max_value`) ??
                            errorOf(`items.${String(index)}.target_value`)
                          }
                          testId="quality-err-item-bounds"
                        />
                      </div>
                    ) : null}

                    {item.check_type === "checklist" ? (
                      <Field label={t("quality.editor.itemChecklist")} wide>
                        <input
                          value={item.checklist_keys}
                          data-testid="quality-item-checklist"
                          placeholder={t("quality.editor.itemChecklistHint")}
                          onChange={(e) => {
                            patchItem(index, { checklist_keys: e.target.value });
                          }}
                        />
                        <FieldError
                          message={errorOf(`items.${String(index)}.checklist`)}
                          testId="quality-err-item-checklist"
                        />
                      </Field>
                    ) : null}

                    <div className="quality-flags">
                      <label className="quality-toggle">
                        <input
                          type="checkbox"
                          checked={item.is_required}
                          data-testid="quality-item-required"
                          onChange={(e) => {
                            patchItem(index, { is_required: e.target.checked });
                          }}
                        />
                        <span>{t("quality.editor.itemRequired")}</span>
                      </label>
                      <label className="quality-toggle">
                        <input
                          type="checkbox"
                          checked={item.photo_required}
                          data-testid="quality-item-photo"
                          onChange={(e) => {
                            patchItem(index, { photo_required: e.target.checked });
                          }}
                        />
                        <span>{t("quality.editor.itemPhoto")}</span>
                      </label>
                      {item.photo_required || item.check_type === "photo" ? (
                        <Field label={t("quality.editor.itemMinPhotos")}>
                          <input
                            inputMode="numeric"
                            value={item.min_photos}
                            data-testid="quality-item-min-photos"
                            onChange={(e) => {
                              patchItem(index, { min_photos: e.target.value });
                            }}
                          />
                          <FieldError
                            message={errorOf(`items.${String(index)}.min_photos`)}
                            testId="quality-err-item-photos"
                          />
                        </Field>
                      ) : null}
                      {/* §2.3 — measurement rows flagged here are printed on the CoA
                    documents 07-E renders. Offered only where it can mean
                    something: a pass/fail tick has no VALUE to print. */}
                      {item.check_type === "measurement" ? (
                        <label className="quality-toggle">
                          <input
                            type="checkbox"
                            checked={item.appears_on_coa}
                            data-testid="quality-item-coa"
                            onChange={(e) => {
                              patchItem(index, { appears_on_coa: e.target.checked });
                            }}
                          />
                          <span>{t("quality.editor.itemCoa")}</span>
                        </label>
                      ) : null}
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ol>
        <Button
          size="sm"
          data-testid="quality-item-add"
          onClick={() => {
            setDirty(true);
            setDraft((prior) => ({
              ...prior,
              items: [
                ...prior.items,
                {
                  key: nextKey(),
                  name: "",
                  // A brand-new item has no translations to carry.
                  name_i18n: {},
                  check_type: "pass_fail",
                  uom: "",
                  min_value: "",
                  target_value: "",
                  max_value: "",
                  checklist_keys: "",
                  photo_required: false,
                  min_photos: "1",
                  is_required: true,
                  appears_on_coa: false,
                },
              ],
            }));
          }}
        >
          {t("quality.editor.addItem")}
        </Button>
      </fieldset>

      {/* ---- operator instructions (§2.1 instructions_i18n) ---- */}
      <fieldset className="quality-group">
        <legend>{t("quality.editor.instructions")}</legend>
        <p className="quality-hint">{t("quality.editor.instructionsHint")}</p>
        {SUPPORTED_LOCALES.filter((locale) => INSTRUCTIONS_SHOWN.has(locale)).map(
          instructionsField,
        )}
        {/* Rev-7: the languages past English and Russian sit behind one
            disclosure, open when one of them already holds text. */}
        <details
          className="quality-locale-more"
          open={moreLanguages.open}
          onToggle={moreLanguages.onToggle}
        >
          <summary>{t("quality.editor.otherLanguages")}</summary>
          {SUPPORTED_LOCALES.filter((locale) => !INSTRUCTIONS_SHOWN.has(locale)).map(
            instructionsField,
          )}
        </details>
      </fieldset>

      <div className="quality-toolbar">
        <Button
          variant="primary"
          data-testid="quality-editor-save"
          busy={busy}
          busyLabel={t("quality.common.saving")}
          onClick={save}
        >
          {mode === "edit" ? t("quality.editor.saveVersion") : t("quality.editor.saveNew")}
        </Button>
        <Button
          data-testid="quality-editor-cancel"
          onClick={() => {
            setDirty(false);
            void navigate({ to: "/quality/control-points", search: {} });
          }}
        >
          {t("quality.common.cancel")}
        </Button>
      </div>

      {showErrors && Object.keys(errors).length > 0 ? (
        <p className="quality-error-note" role="alert" data-testid="quality-editor-invalid">
          {t("quality.editor.invalid", { count: Object.keys(errors).length })}
        </p>
      ) : null}
      {create.isError || version.isError ? (
        <p className="quality-error-note" role="alert" data-testid="quality-editor-failed">
          {t("quality.editor.saveFailed")}
          {(() => {
            const id = requestIdOf(create.error ?? version.error);
            return id !== undefined ? ` · ${t("quality.common.requestId")}: ${id}` : "";
          })()}
        </p>
      ) : null}
    </section>
  );
}

function FieldError({ message, testId }: { message: string | undefined; testId: string }) {
  if (message === undefined) return null;
  return (
    <span className="quality-error-note" role="alert" data-testid={testId}>
      {message}
    </span>
  );
}
